import { useState, useEffect } from "react";
import { db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  onSnapshot
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface BrokerDocument {
  id: string;
  name: string;
  type: 'contract' | 'agreement' | 'financial' | 'other';
  size: number;
  url: string;
  uploadedAt: any;
  dealId?: string;
  sellerId?: string;
}

export function useBrokerDocuments() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<BrokerDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const docsRef = collection(db, "brokerDocuments");
    const docsQuery = query(
      docsRef,
      where("brokerId", "==", user.id),
      orderBy("uploadedAt", "desc")
    );

    const unsubscribe = onSnapshot(docsQuery, (snapshot) => {
      const docsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BrokerDocument[];
      setDocuments(docsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching documents:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const uploadDocument = async (file: File, type: BrokerDocument['type'], dealId?: string, sellerId?: string) => {
    if (!user) return null;

    try {
      // For now, we create a document record (actual file upload would use Firebase Storage)
      const docRef = await addDoc(collection(db, "brokerDocuments"), {
        name: file.name,
        type,
        size: file.size,
        url: "", // Would be set after Firebase Storage upload
        brokerId: user.id,
        dealId: dealId || null,
        sellerId: sellerId || null,
        uploadedAt: serverTimestamp()
      });

      toast({
        title: "Document uploaded",
        description: `${file.name} has been uploaded successfully.`
      });

      return docRef.id;
    } catch (error) {
      console.error("Error uploading document:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload document. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  const deleteDocument = async (documentId: string) => {
    try {
      await deleteDoc(doc(db, "brokerDocuments", documentId));
      toast({
        title: "Document deleted",
        description: "The document has been removed."
      });
      return true;
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Delete failed",
        description: "Failed to delete document. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  const getDocumentsByType = (type: BrokerDocument['type']) => {
    return documents.filter(doc => doc.type === type);
  };

  return {
    documents,
    loading,
    uploadDocument,
    deleteDocument,
    getDocumentsByType
  };
}
